import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { clearContentCache } from "@/lib/useContent";
import { formatApiError } from "@/context/AuthContext";
import {
    Save, RotateCcw, Plus, Trash2, Edit3, Check, X,
    FileText, Film, BookOpen, MapPin, Inbox, Image as ImageIcon, Upload, Navigation, UserCheck, Radio, CalendarClock, Ban, MessageSquareQuote, Star, Mic, Play,
} from "lucide-react";
import { Field } from "../shared.jsx";

const TESTI_STATUS = {
    pending: { label: "Pending", tone: "text-yellow-300 border-yellow-300/40" },
    approved: { label: "Approved", tone: "text-emerald-300 border-emerald-300/40" },
    hidden: { label: "Hidden", tone: "text-white/40 border-white/20" },
};

export default function TestimonialsManager() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");

    const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/admin/testimonials/${id}`, { status });
            toast.success(`Message ${status}.`);
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this message?")) return;
        try {
            await api.delete(`/admin/testimonials/${id}`);
            toast.success("Deleted.");
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const counts = items.reduce((a, t) => { a[t.status] = (a[t.status] || 0) + 1; return a; }, {});
    const filtered = filter === "all" ? items : items.filter((t) => t.status === filter);

    return (
        <div data-testid="testimonials-manager">
            <div className="overline mb-3">Visitor wall</div>
            <p className="text-sm text-[var(--sb-text-dim)] max-w-xl mb-6">
                Messages are held here for moderation. Only <strong>Approved</strong> ones appear on the public wall.
            </p>

            <div className="flex flex-wrap items-center gap-2 mb-6">
                <button onClick={() => setFilter("all")} className={`text-xs uppercase tracking-[0.22em] px-3 py-2 border ${filter === "all" ? "border-[var(--sb-brand)] text-[var(--sb-text)]" : "border-[var(--sb-border)] text-[var(--sb-text-dim)]"}`}>
                    All · {items.length}
                </button>
                {Object.keys(TESTI_STATUS).map((s) => (
                    <button
                        key={s}
                        onClick={() => setFilter(s)}
                        className={`text-xs uppercase tracking-[0.22em] px-3 py-2 border ${filter === s ? "border-[var(--sb-brand)] text-[var(--sb-text)]" : "border-[var(--sb-border)] text-[var(--sb-text-dim)]"}`}
                        data-testid={`testimonials-filter-${s}`}
                    >
                        {TESTI_STATUS[s].label} · {counts[s] || 0}
                    </button>
                ))}
            </div>

            {filtered.length === 0 ? (
                <div className="card p-10 text-center text-[var(--sb-text-dim)]">
                    No messages in this view yet.
                </div>
            ) : (
                <div className="space-y-3">
                    {filtered.map((t) => (
                        <div key={t.id} className="card p-5 grid grid-cols-1 lg:grid-cols-12 gap-4" data-testid={`admin-testimonial-${t.id}`}>
                            <div className="lg:col-span-8">
                                <div className="flex items-center gap-3 mb-2 flex-wrap">
                                    <span className={`text-[10px] tracking-[0.22em] uppercase border px-2 py-1 ${TESTI_STATUS[t.status]?.tone}`}>
                                        {TESTI_STATUS[t.status]?.label}
                                    </span>
                                    <div className="flex items-center gap-0.5 text-[var(--sb-brand)]">
                                        {[...Array(5)].map((_, i) => (
                                            <Star key={i} size={12} className={i < (t.rating || 5) ? "text-[var(--sb-brand)]" : "text-[var(--sb-border)]"} fill={i < (t.rating || 5) ? "currentColor" : "none"} />
                                        ))}
                                    </div>
                                    <span className="text-xs text-[var(--sb-text-muted)]">{new Date(t.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                                </div>
                                <p className="font-serif text-lg text-[var(--sb-text)] mb-2 leading-snug">&ldquo;{t.message}&rdquo;</p>
                                <div className="text-sm text-[var(--sb-text-dim)]">
                                    <strong>{t.name}</strong>
                                    {t.location && <> · {t.location}</>}
                                    {t.email && <> · <a href={`mailto:${t.email}`} className="hover:text-[var(--sb-brand)]">{t.email}</a></>}
                                    {t.source_page && <> · from <code className="text-xs">{t.source_page}</code></>}
                                </div>
                            </div>
                            <div className="lg:col-span-4 flex flex-wrap gap-2 lg:justify-end items-start">
                                {t.status !== "approved" && (
                                    <button onClick={() => setStatus(t.id, "approved")} className="text-xs px-3 py-2 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid={`testimonial-approve-${t.id}`}>Approve</button>
                                )}
                                {t.status !== "hidden" && (
                                    <button onClick={() => setStatus(t.id, "hidden")} className="text-xs px-3 py-2 border border-white/20 text-white/60 hover:bg-white/5">Hide</button>
                                )}
                                {t.status !== "pending" && (
                                    <button onClick={() => setStatus(t.id, "pending")} className="text-xs px-3 py-2 border border-yellow-500/30 text-yellow-300 hover:bg-yellow-500/10">Reopen</button>
                                )}
                                <button onClick={() => remove(t.id)} className="text-xs px-3 py-2 border border-[var(--sb-border)] text-[var(--sb-text-muted)] hover:text-red-400 hover:border-red-400/40"><Trash2 size={12} className="inline" /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/* Things to do editor                                                        */
